import ListContentWrapper from "components/ListContentWrapper";
import SearchInput from "components/SearchInput";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import TierInfo from "components/TierInfo";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import useCanCreateScan from "shared/Scan/access/useCanCreateScan";
import useScanCreationRequirements from "shared/Scan/useScanCreationRequirements";
import { BillingTierFeature } from "types/generated";

import ResourcesHeader from "../Resources/Header";
import useTierFeature from "../hooks/useTierFeature";
import ScansEmptyState from "./components/EmptyState";
import ScheduleScanButton from "./components/ScheduleScanButton";
import SCANS_TABLE_CELLS from "./components/Table/Cells";
import {
  SCANS_FILTERS_ORDER_KEY,
  SCANS_LIMIT,
  SCANS_LIMIT_STORAGE_KEY,
  SCANS_PAGINATION_OPTIONS,
  SCANS_SORT_CONFIG,
} from "./components/Table/constants";
import useScansTableStore from "./components/Table/useStore";
import useScans from "./useScans/useScans";

const BILLING_TIER_MESSAGE = "Upgrade your plan to use Cloud scanning.";
const NO_INTEGRATION_MESSAGE =
  "Connect an AWS integration that generates credentials in a private worker before scheduling a scan.";
const NO_WORKER_POOL_MESSAGE =
  "Attach a private worker pool to a space before you can schedule a scan.";

const Scans = () => {
  const hasScansFeature = useTierFeature(BillingTierFeature.Scans);
  const canCreateScan = useCanCreateScan();

  const {
    hasAttachableIntegration,
    hasAttachableWorkerPool,
    loading: loadingRequirements,
    error: requirementsError,
    refetch: refetchRequirements,
  } = useScanCreationRequirements();
  // Fail open while the check is loading or errored — don't block scheduling on a slow/failed read.
  const hasNoAttachableIntegration =
    !loadingRequirements && !requirementsError && !hasAttachableIntegration;
  const hasNoAttachableWorkerPool =
    !loadingRequirements && !requirementsError && !hasAttachableWorkerPool;

  const isScheduleScanDisabled =
    !hasScansFeature ||
    !canCreateScan.result ||
    hasNoAttachableIntegration ||
    hasNoAttachableWorkerPool;
  const scheduleScanTooltipMessage = !hasScansFeature
    ? BILLING_TIER_MESSAGE
    : (canCreateScan.message ??
      (hasNoAttachableIntegration ? NO_INTEGRATION_MESSAGE : undefined) ??
      (hasNoAttachableWorkerPool ? NO_WORKER_POOL_MESSAGE : undefined));

  const {
    scans,
    error,
    loadingContent,
    hasRows,
    hasNoResults,
    loadScans,
    endCursor,
    startCursor,
    totalCount,
    paginationResetKey,
  } = useScans({ skip: !hasScansFeature });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadScans,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: scans.length,
    initialLimit: SCANS_LIMIT,
    storageKey: SCANS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const ErrorContent = useErrorHandle(error);
  const showSearch = hasScansFeature && (hasRows || hasNoResults);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <ResourcesHeader />

      <PageInfoHeader title="Cloud scanning">
        {showSearch && (
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.SCANS}
            filtersOrderSettingsKey={SCANS_FILTERS_ORDER_KEY}
          />
        )}
        <ScheduleScanButton
          disabled={isScheduleScanDisabled}
          tooltipMessage={scheduleScanTooltipMessage}
        />
      </PageInfoHeader>

      <TierInfo
        type="callout"
        variant="promo"
        title="Upgrade plan to use Cloud scanning"
        feature={BillingTierFeature.Scans}
      >
        Cloud scanning is not available in your current plan. Upgrade to discover resources running
        outside of Spacelift's management.
      </TierInfo>

      {hasScansFeature ? (
        <ListContentWrapper>
          {(hasRows || loadingContent) && (
            <>
              <TableSimple
                data={scans}
                loading={loadingContent}
                store={useScansTableStore}
                ariaLabel="Cloud scans table"
                cellPerColumn={SCANS_TABLE_CELLS}
                filtersStorageKey={SCANS_FILTERS_ORDER_KEY}
                rowsPerPage={rowsPerPage}
                sortConfig={SCANS_SORT_CONFIG}
                hasPagination
              />
              <TablePagination
                from={from}
                to={to}
                loading={loadingContent}
                goToNextPage={goToNextPage}
                setRowsPerPage={setRowsPerPage}
                goToPrevPage={goToPrevPage}
                goToFirstPage={goToFirstPage}
                goToLastPage={goToLastPage}
                rowsPerPage={rowsPerPage}
                itemsCount={totalCount}
                lastPage={lastPage}
                page={page}
                paginationOptions={SCANS_PAGINATION_OPTIONS}
              />
            </>
          )}

          {!hasRows && !loadingContent && (
            <ScansEmptyState
              hasNoResults={hasNoResults}
              isScheduleScanDisabled={isScheduleScanDisabled}
              scheduleScanTooltipMessage={scheduleScanTooltipMessage}
              hasNoAttachableIntegration={hasNoAttachableIntegration}
              hasNoAttachableWorkerPool={hasNoAttachableWorkerPool}
              onRequirementCreated={() => refetchRequirements()}
            />
          )}
        </ListContentWrapper>
      ) : (
        <ScansEmptyState
          isScheduleScanDisabled={isScheduleScanDisabled}
          scheduleScanTooltipMessage={scheduleScanTooltipMessage}
        />
      )}
    </>
  );
};

export default Scans;
